import React, { useMemo, useState } from "react"
import { faBell, faCalendarDay, faClose, faSearch } from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../components/Button/Button"
import { GenericDropDown } from "../../components/Dropdown/Dropdown"
import InputField from "../../components/InputField/InputField"
import ModalPortal from "../../components/Modal/ModalPortal"
import { SimpleToggle } from "../../components/SimpleToggle/SimpleToggle"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import { EPlatformNoticeAudienceType } from "../../Enum/EPlatformNoticeAudienceType"
import { EPlatformNoticeCategoryType } from "../../Enum/EPlatformNoticeCategoryType"
import { EPlatformNoticePriorityType } from "../../Enum/EPlatformNoticePriorityType"
import { EPlatformNoticeRecipientType } from "../../Enum/EPlatformNoticeRecipientType"
import { usePlatformNotices } from "../../hooks/PlatformNotice/usePlatformNotices"
import { PlatformNotice } from "../../Objects/PlatformNotice/PlatformNotice"
import { PlatformNoticeRecipient } from "../../Objects/PlatformNotice/PlatformNoticeRecipient"
import { SavePlatformNotice } from "../../Objects/PlatformNotice/SavePlatformNotice"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { useTranslation } from "react-i18next"

interface PlatformNoticePopupProps {
  notice: PlatformNotice
  recipients: PlatformNoticeRecipient[]
  onClose: () => void
  onSuccess: () => void
}

const toInputDate = (date?: Date) => {
  if (!date) return ""

  const year = date.getFullYear()

  const month = `${date.getMonth() + 1}`.padStart(2, "0")

  const day = `${date.getDate()}`.padStart(2, "0")

  return `${year}-${month}-${day}`
}

const fromInputDate = (date: string | undefined, endOfDay = false) => {
  if (!date) return undefined

  return `${date}T${endOfDay ? "23:59:59" : "00:00:00"}.000Z`
}

const getSelectedRecipientType = (audienceType: EPlatformNoticeAudienceType) => {
  switch (audienceType) {
    case EPlatformNoticeAudienceType.SelectedRestaurants:
      return EPlatformNoticeRecipientType.Restaurant
    case EPlatformNoticeAudienceType.SelectedCouriers:
      return EPlatformNoticeRecipientType.Courier
    case EPlatformNoticeAudienceType.SelectedResellers:
      return EPlatformNoticeRecipientType.Reseller
    default:
      return undefined
  }
}

export const PlatformNoticePopup: React.FC<PlatformNoticePopupProps> = ({ notice, recipients, onClose, onSuccess }) => {
  const { t } = useTranslation("platformNotice")

  const { isSaving, saveAdminNotice } = usePlatformNotices()

  const [title, setTitle] = useState(notice.title)

  const [message, setMessage] = useState(notice.message)

  const [categoryType, setCategoryType] = useState(notice.categoryType)

  const [priorityType, setPriorityType] = useState(notice.priorityType)

  const [audienceType, setAudienceType] = useState(notice.audienceType)

  const [isActive, setIsActive] = useState(notice.isActive)

  const [publishDate, setPublishDate] = useState(notice.id > 0 ? toInputDate(notice.publishAtUtc) : "")

  const [expiresDate, setExpiresDate] = useState(toInputDate(notice.expiresAtUtc))

  const [targetUserIds, setTargetUserIds] = useState<string[]>(notice.targetUserIds)

  const [recipientSearch, setRecipientSearch] = useState("")

  const selectedRecipientType = getSelectedRecipientType(audienceType)

  const recipientsByAudience = useMemo(
    () => (selectedRecipientType ? recipients.filter((item) => item.recipientType === selectedRecipientType) : []),
    [recipients, selectedRecipientType],
  )

  const filteredRecipients = useMemo(() => {
    const search = recipientSearch.trim().toLowerCase()

    if (!search) return recipientsByAudience

    return recipientsByAudience.filter((item) =>
      `${item.companyName} ${item.slug} ${item.userId} ${item.recipientKey}`.toLowerCase().includes(search),
    )
  }, [recipientSearch, recipientsByAudience])

  const selectedTargetKeys = useMemo(
    () => new Set(recipientsByAudience.map((item) => item.recipientKey)),
    [recipientsByAudience],
  )

  const validTargetUserIds = useMemo(
    () => targetUserIds.filter((item) => selectedTargetKeys.has(item)),
    [selectedTargetKeys, targetUserIds],
  )

  const categoryOptions = Object.values(EPlatformNoticeCategoryType).map((value) => ({
    value,
    label: t(`category.${value}`),
  }))

  const priorityOptions = Object.values(EPlatformNoticePriorityType).map((value) => ({
    value,
    label: t(`priority.${value}`),
  }))

  const audienceOptions = Object.values(EPlatformNoticeAudienceType).map((value) => ({
    value,
    label: t(`audience.${value}`),
  }))

  const handleToggleTarget = (recipientKey: string) => {
    setTargetUserIds((current) =>
      current.includes(recipientKey) ? current.filter((item) => item !== recipientKey) : [...current, recipientKey],
    )
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const request = new SavePlatformNotice({
      title,
      message,
      categoryType,
      priorityType,
      audienceType,
      isActive,
      publishAtUtc: fromInputDate(publishDate),
      expiresAtUtc: fromInputDate(expiresDate, true),
      targetUserIds: validTargetUserIds,
    })

    saveAdminNotice(request, notice.id > 0 ? notice.id : undefined, () => {
      onClose()
      onSuccess()
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={notice.id > 0 ? t("admin_edit_title") : t("admin_create_title")}
              icon={faBell}
              iconColor={THEME.colors.iconBGColor}
              path={t("admin_create_description")}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.platformNoticePopup.button.onClose"
                />
              }
            />

            <form onSubmit={handleSubmit} className="mt-3 space-y-3 p-2">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <InputField
                  type="text"
                  value={title}
                  icon={faBell}
                  placeholder={t("field_title")}
                  onInvalidMessage={t("field_title_invalid")}
                  onChange={(event) => setTitle(event.target.value)}
                  required
                  maxLength={140}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.platformNoticePopup.input.title"
                />

                <GenericDropDown
                  data={categoryOptions}
                  initialState={categoryType}
                  handlerFuncion={(event) => setCategoryType(event.target.value as EPlatformNoticeCategoryType)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.platformNoticePopup.select.category"
                />

                <GenericDropDown
                  data={priorityOptions}
                  initialState={priorityType}
                  handlerFuncion={(event) => setPriorityType(event.target.value as EPlatformNoticePriorityType)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.platformNoticePopup.select.priority"
                />

                <GenericDropDown
                  data={audienceOptions}
                  initialState={audienceType}
                  handlerFuncion={(event) => setAudienceType(event.target.value as EPlatformNoticeAudienceType)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.platformNoticePopup.select.audience"
                />

                <InputField
                  type="date"
                  value={publishDate}
                  icon={faCalendarDay}
                  placeholder={t("field_publish_date")}
                  onChange={(event) => setPublishDate(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.platformNoticePopup.input.publishDate"
                />

                <InputField
                  type="date"
                  value={expiresDate}
                  icon={faCalendarDay}
                  placeholder={t("field_expires_date")}
                  onChange={(event) => setExpiresDate(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.platformNoticePopup.input.expiresDate"
                />
              </div>

              <SimpleToggle
                title={t("field_active")}
                value={isActive}
                onChange={setIsActive}
                descriptionKey="interactionDescriptions:pages.adminDashboard.platformNoticePopup.toggle.active"
              />

              <div>
                <label className="mb-1 ml-2 block text-sm font-medium text-gray-600" htmlFor="platform-notice-message">
                  {t("field_message")}
                </label>

                <textarea
                  id="platform-notice-message"
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  required
                  maxLength={6000}
                  className="min-h-40 w-full rounded-lg border border-gray-300 p-3 text-sm text-slate-800 outline-none ring-2 ring-gray-200 transition focus:border-orange-400 focus:ring-orange-500"
                  placeholder={t("field_message_placeholder")}
                  data-interaction-description-key="interactionDescriptions:pages.adminDashboard.platformNoticePopup.textarea.message"
                />
                <p className="mt-1 text-right text-xs font-bold text-slate-400">{message.length}/6000</p>
              </div>

              {selectedRecipientType && (
                <div className="rounded-lg border border-slate-200 p-3">
                  <InputField
                    type="text"
                    value={recipientSearch}
                    icon={faSearch}
                    placeholder={t(`recipient_search.${selectedRecipientType}`)}
                    onChange={(event) => setRecipientSearch(event.target.value)}
                    descriptionKey="interactionDescriptions:pages.adminDashboard.platformNoticePopup.input.recipientSearch"
                  />

                  <div className="mt-3 max-h-56 space-y-2 overflow-y-auto pr-1">
                    {filteredRecipients.map((recipient) => (
                      <label
                        key={recipient.recipientKey}
                        className="flex cursor-pointer items-start gap-2 rounded-md border border-slate-100 p-2 text-sm transition hover:bg-orange-50"
                      >
                        <input
                          type="checkbox"
                          checked={validTargetUserIds.includes(recipient.recipientKey)}
                          onChange={() => handleToggleTarget(recipient.recipientKey)}
                          className="mt-1 h-4 w-4 accent-orange-600"
                        />

                        <span>
                          <span className="block font-black text-slate-800">{recipient.companyName}</span>

                          <span className="block text-xs text-slate-500">{recipient.slug || recipient.userId}</span>
                        </span>
                      </label>
                    ))}
                  </div>

                  <p className="mt-2 text-xs font-bold text-slate-500">
                    {t(`selected_recipients.${selectedRecipientType}`, { count: validTargetUserIds.length })}
                  </p>
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  typeButton="submit"
                  label={isSaving ? t("saving") : notice.id > 0 ? t("save_edit") : t("send")}
                  bgColor={THEME.colors.buttonBGSaveColor}
                  disabled={isSaving}
                  iconColor={THEME.colors.buttonFGSaveColor}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.platformNoticePopup.button.save"
                />
              </div>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
